import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { PageHeader, Field, inputCls } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";

const TERMS = [{ m: 6, apr: 0 }, { m: 12, apr: 0 }, { m: 24, apr: 9.99 }, { m: 36, apr: 9.99 }];

const payment = (amt: number, m: number, apr: number) => {
  if (!apr) return amt / m;
  const r = apr / 100 / 12;
  return (amt * r) / (1 - Math.pow(1 + r, -m));
};

export default function Financing() {
  const [amount, setAmount] = useState(4000);
  const [term, setTerm] = useState(12);
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [done, setDone] = useState(false);
  useMeta("Jewelry Financing | Somnio.Co", "Pay for your engagement ring over 6 to 36 months. Estimate monthly payments and apply in minutes.");
  const t = TERMS.find((x) => x.m === term)!;
  const mo = payment(amount, term, t.apr);

  const apply = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/financing/apply", { ...f, amount, term_months: term }); setDone(true); } catch (x) { toast.error(errMsg(x)); }
  };

  return (
    <div data-testid="financing-page">
      <PageHeader eyebrow="Pay Over Time" title="Financing" intro="Spread the cost of your ring over 6 to 36 months. Estimates below are indicative; final terms are confirmed by our financing partner after approval." />
      <div className="max-w-[1200px] mx-auto px-5 md:px-12 grid lg:grid-cols-2 gap-12">
        <section className="bg-white border border-neutral-200 p-8" data-testid="financing-calculator">
          <p className="eyebrow mb-6">Payment Estimator</p>
          <Field label={`Purchase amount: ${money(amount)}`}>
            <input type="range" min={500} max={30000} step={100} value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-full accent-neutral-900" data-testid="financing-amount-slider" />
          </Field>
          <div className="grid grid-cols-4 gap-2 mt-8">
            {TERMS.map((x) => (
              <button key={x.m} onClick={() => setTerm(x.m)} data-testid={`financing-term-${x.m}`} className={`py-4 border text-center transition-colors ${term === x.m ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}>
                <span className="block font-display text-2xl">{x.m}</span><span className="text-[10px] tracking-widest uppercase">months · {x.apr}% APR</span>
              </button>
            ))}
          </div>
          <div className="mt-10 border-t border-neutral-200 pt-8">
            <p className="eyebrow text-neutral-500">Estimated monthly</p>
            <p className="font-display text-5xl mt-2" data-testid="financing-monthly">{money(Math.ceil(mo))}<span className="text-lg text-neutral-500">/mo</span></p>
            <p className="text-xs text-neutral-500 mt-3">Total repayable approx. {money(Math.round(mo * term))}. Subject to credit approval.</p>
          </div>
        </section>
        <section>
          {done ? (
            <div className="bg-[var(--c-muted)] p-10" data-testid="financing-success"><p className="font-display text-3xl">Thank you</p><p className="mt-4 text-sm text-neutral-600">We've received your enquiry and emailed you a confirmation. A specialist will be in touch within one business day.</p></div>
          ) : (
            <form onSubmit={apply} className="space-y-6" data-testid="financing-form">
              <p className="font-display text-3xl font-light">Start your application</p>
              <Field label="Full name"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="financing-name-input" /></Field>
              <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="financing-email-input" /></Field>
              <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="financing-phone-input" /></Field>
              <button className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="financing-submit-button">Submit Enquiry</button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
